import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';

import {AuthFacade} from 'src/app/store/auth-state/+state/auth.facade';
import {
	BehaviorSubject,
	catchError,
	combineLatest,
	debounceTime,
	filter,
	interval,
	map,
	Observable,
	of,
	Subject,
	switchMap,
	takeUntil
} from 'rxjs';

import {ManagerFacade} from 'src/app/store/manager/+state/manager.facade';
import {OrderStatus} from 'src/app/models/OrderStatus';
import {OrderType} from 'src/app/models/OrderType';
import * as moment from 'moment';
import {Socket} from 'ngx-socket-io';
import {SocketRole} from 'src/app/models/SocketRoles';
import Order from 'src/app/models/Order';
import {OrderService, STATUS_CHANGE} from 'src/app/services/order.service';
import {Table} from 'src/app/models/Table';
import {NavigationService} from 'src/app/services/navigation.service';
import {MatDialog} from '@angular/material/dialog';
import {OrderPool} from 'src/app/models/OrderPool';
import {PreorderType} from 'src/app/models/PreorderType';
import {Venue} from 'src/app/models/Venue';
import {ApiService} from 'src/app/services/api.service';
import {ToastrService} from 'ngx-toastr';
import {OrderChannelsFilter} from 'src/app/models/OrderChannels';
import {PaymentMethod} from 'src/app/models/PaymentMethod';
import {PrinterService} from 'src/app/services/printer.service';
import {checkIfOrderPool} from 'src/app/utils/utils';

@Component({
	selector: 'app-manger',
	templateUrl: './manger.page.html',
	styleUrls: ['./manger.page.scss']
})
export class ManagerPage implements OnDestroy, OnInit {
	@ViewChild('scrollableContent', {static: false})
	scrollableContent!: ElementRef;

	private readonly onDestroy$ = new Subject<void>();
	private readonly stopListeningSocketForTable$ = new Subject<void>();
	public readonly updateStatusLoading$ =
		this.managerFacade.updateStatusLoading$;
	public venue$ = this.managerFacade.venue$;
	public venueData$ = new BehaviorSubject<Venue | null>(null);
	public checkStatus$: Observable<boolean>;
	public posStatusObservable$ = this.managerFacade.posHealthStatus$;
	public posStatus$$ = new BehaviorSubject<boolean | null>(null);

	private _showError$ = new BehaviorSubject<{
		warning: string;
		error: string;
		id: string;
	} | null>(null);
	public showErroMessage$ = this._showError$.pipe(debounceTime(2000));
	public loadingOrders: string[] = [];
	public orders$ = combineLatest(
		this.managerFacade.orders$,
		this.managerFacade.ordersPool$,
		this.managerFacade.orderStatusFiler$,
		this.managerFacade.orderChannelsFilter$
	).pipe(
		map(([ordersValue, orderPoolValue, status, orderChannels]) => {
			this.orderChannels = orderChannels;
			let orders = (JSON.parse(JSON.stringify(ordersValue)) as Order[]).filter(
				it => {
					if (it?.preorder?.type === PreorderType.FOOD_SPOT) {
						return false;
					} else {
						return true;
					}
				}
			);
			let orderPool = JSON.parse(JSON.stringify(orderPoolValue)) as OrderPool[];

			orderPool = orderPool.filter(
				() => orderChannels.length === 0 || orderChannels.includes('foodspot')
			);

			const statusOrder = [
				null,
				OrderStatus.AWAITING_CONFIRMATION,
				OrderStatus.IN_PREPARATION,
				OrderStatus.READY,
				OrderStatus.DONE
			];
			if (orders.length > 0)
				orders.sort((a, b) => {
					return (
						statusOrder.indexOf(a?.status ?? null) -
						statusOrder.indexOf(b?.status ?? null)
					);
				});
			const today = moment().startOf('day'); // Start of today's day (midnight)

			orders = orders.filter(it => {
				if (it.asap) {
					return true;
				}
				return moment(it.orderAt).isSame(today, 'day');
			});
			if (orderPool.length > 0) {
				orderPool.sort((a, b) => {
					return (
						statusOrder.indexOf(a?.orderStatus ?? null) -
						statusOrder.indexOf(b?.orderStatus ?? null)
					);
				});
			}

			if (status != OrderStatus.DONE) {
				orders = orders.filter(it => it.status != OrderStatus.DONE);
				orderPool = orderPool.filter(it => it.orderStatus != OrderStatus.DONE);
			}

			if (orderChannels.length > 0) {
				orders = this.filterOrderChannel(orders, orderChannels);
			}

			if (!status) {
				orders = orders.filter(
					it =>
						it.status === OrderStatus.IN_PREPARATION ||
						it.status === OrderStatus.AWAITING_CONFIRMATION ||
						it.status === OrderStatus.READY
				);
				orderPool = orderPool
					.filter(
						it =>
							it.orderStatus === OrderStatus.IN_PREPARATION ||
							it.orderStatus === OrderStatus.AWAITING_CONFIRMATION ||
							it.orderStatus === OrderStatus.READY
					)
					.map(it => {
						it.orders = it.orders.filter(
							it =>
								it.status === OrderStatus.IN_PREPARATION ||
								it.status === OrderStatus.AWAITING_CONFIRMATION ||
								it.status === OrderStatus.READY
						);
						return it;
					});

				return [...orderPool, ...orders]
					.sort(
						(a, b) =>
							new Date(this.checkIfOrder(a) ? a.orderAt : '').getTime() -
							new Date(this.checkIfOrder(b) ? b.orderAt : '').getTime()
					)
					.sort((a, b) => {
						return (
							statusOrder.indexOf(
								this.checkIfOrder(a)
									? a.status
									: this.checkIfOrderPool(a)
									? a.orderStatus
									: null
							) -
							statusOrder.indexOf(
								this.checkIfOrder(b)
									? b.status
									: this.checkIfOrderPool(b)
									? b.orderStatus
									: null
							)
						);
					});
			}

			orders = orders.filter(it => it.status == status);
			orderPool = orderPool.filter(it => it.orderStatus == status);

			return [...orderPool, ...orders]
				.sort(
					(a, b) =>
						new Date(this.checkIfOrder(a) ? a.orderAt : '').getTime() -
						new Date(this.checkIfOrder(b) ? b.orderAt : '').getTime()
				)
				.sort((a, b) => {
					return (
						statusOrder.indexOf(
							this.checkIfOrder(a)
								? a.status
								: this.checkIfOrderPool(a)
								? a.orderStatus
								: null
						) -
						statusOrder.indexOf(
							this.checkIfOrder(b)
								? b.status
								: this.checkIfOrderPool(b)
								? b.orderStatus
								: null
						)
					);
				});
		})
	);
	public statusChangeENUM = STATUS_CHANGE;
	public orderChannels: OrderChannelsFilter = [];
	public soundOnKey = 'soundOn';
	public soundOn$$ = new BehaviorSubject<boolean>(
		JSON.parse(localStorage.getItem(this.soundOnKey) ?? 'true')
	);
	checkIfOrderPool = checkIfOrderPool;
	constructor(
		private authFacade: AuthFacade,
		private managerFacade: ManagerFacade,
		private socket: Socket,
		private orderService: OrderService,
		private navService: NavigationService,
		public dialog: MatDialog,
		private apiService: ApiService,
		private toaster: ToastrService,
		private printer: PrinterService
	) {}

	ngOnInit(): void {
		this._showError$.subscribe(v => {
			console.log(v);
		});
		this.authFacade.userData$
			.pipe(
				filter(userValue => !!userValue),
				map(userData => {
					return userData?.venues[0] ?? null;
				}),
				takeUntil(this.onDestroy$)
			)
			.subscribe(venueId => {
				if (venueId) {
					this.loadDataByUser(venueId);
				}
			});

		this.posStatusObservable$
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(status => {
				this.posStatus$$.next(status);
			});

		// this.venue$
		// 	.pipe(
		// 		filter((venue): venue is Venue => !!venue),
		// 		tap(venue => this.venueData$.next(venue)),
		// 		switchMap(venue => this.pollPosStatus(venue._id, this.onDestroy$)),
		// 		takeUntil(this.onDestroy$)
		// 	)
		// 	.subscribe(status => {
		// 		console.log('POS status:', status);
		// 		if (status !== null) {
		// 			this.managerFacade.updatePosHealthStatus(status);
		// 		}
		// 	});

		this.managerFacade.tables$
			.pipe(
				filter(device => device.length > 0),
				takeUntil(this.stopListeningSocketForTable$ || this.onDestroy$)
			)
			.subscribe(tables => {
				console.log('table', tables);
				if (tables.length > 0) {
					this.listenSocket(tables);
				}
			});
		this.checkStatus$ = this.venue$.pipe(
			map(venue => {
				if (!venue) {
					return false;
				}
				return this.isUberDirectDispatchForFoodspotEnabled(venue);
			}),
			takeUntil(this.onDestroy$)
		);
		interval(30000)
			.pipe(switchMap(() => combineLatest([this.checkStatus$, this.orders$])))
			.subscribe(([checkstatus, orders]) => {
				if (
					checkstatus &&
					orders.length > 0 &&
					orders?.find(it => it?.dndCompanyCode)
				) {
					const orderPools: OrderPool[] = [];
					orders.forEach(it => {
						if (checkIfOrderPool(it)) {
							orderPools.push(it);
						}
					});
					let foundedValueWarning = false;
					if (orderPools.length > 0) {
						const now = moment();
						const threshold = moment().add(20, 'minutes');
						const foundTime = orderPools.find(pool => {
							const estimated = moment(pool?.estimatedAt);
							return (
								pool.orderStatus == OrderStatus.IN_PREPARATION &&
								pool.estimatedAt &&
								estimated.isBetween(now, threshold, undefined, '[)')
							);
						});
						console.log('foudntime', foundTime);
						if (foundTime) {
							this._showError$.next({
								warning: 'Error',
								error: '',
								id: foundTime._id ?? ''
							});
							foundedValueWarning = true;
						} else {
							foundedValueWarning = false;
						}
					}
					let foundedValueError = false;

					if (orderPools.length > 0) {
						const now = moment();
						const threshold = moment().add(30, 'minutes');
						const foundTime = orderPools.find(pool => {
							const estimated = moment(pool.estimatedAt);
							return (
								pool.orderStatus == OrderStatus.IN_PREPARATION &&
								estimated.isBetween(now, threshold, undefined, '[)')
							);
						});
						if (foundTime) {
							this._showError$.next({
								warning: '',
								error: 'Error',
								id: foundTime._id ?? ''
							});
							foundedValueError = true;
						} else {
							foundedValueError = false;
						}
					}
					if (!foundedValueError && !foundedValueWarning) {
						this._showError$.next(null);
					}
				}
			});
	}

	private isUberDirectDispatchForFoodspotEnabled(venue: Venue) {
		if (!venue.deliveryProviders?.uberDirect?.deliverableOrderTypes)
			return false;
		return (
			Array.isArray(venue.deliveryProviders.uberDirect.deliverableOrderTypes) &&
			(venue.deliveryProviders.uberDirect.deliverableOrderTypes.includes(
				PreorderType.FOOD_SPOT
			) ||
				venue.deliveryProviders.uberDirect.deliverableOrderTypes.length === 0)
		);
	}
	private loadDataByUser(venueId: string) {
		this.managerFacade.loadVenueData({venueId});
		this.loadOrdersWithDefaultConfig(venueId);
	}
	ngOnDestroy() {
		this.onDestroy$.next();
	}
	private loadOrdersWithDefaultConfig(venueId: string) {
		this.managerFacade.loadOrders({
			venue: venueId,
			status: [
				OrderStatus.AWAITING_CONFIRMATION,
				OrderStatus.IN_PREPARATION,
				OrderStatus.READY,
				OrderStatus.DONE
			],
			orderTypes: [
				OrderType.TERMINAL,
				OrderType.STANDARD,
				OrderType.VIRTUAL,
				OrderType.PREORDER
			],
			from: moment().subtract(1, 'days').toString(),
			to: moment().add(2, 'days').toString(),
			promoCode: true
		});
		this.managerFacade.loadOrdersPool({
			venue: venueId,
			status: [OrderStatus.IN_PREPARATION, OrderStatus.READY, OrderStatus.DONE],
			orderTypes: [
				OrderType.TERMINAL,
				OrderType.STANDARD,
				OrderType.VIRTUAL,
				OrderType.PREORDER
			],
			from: moment().startOf('day').toString(),
			to: moment().add(2, 'days').toString(),
			promoCode: true
		});
		this.listenSocketOrders(venueId);
	}
	private listenSocketOrders(venueId: string) {
		this.socket.on('connect', () => {
			this.authenticateSocket({
				roles: [SocketRole.COUNTER_DISPLAY, SocketRole.COUNTER_DISPLAY_MAIN],
				venueIds: [venueId]
			});
		});

		this.socket.on('venue-' + venueId + '-orders', (data: any) => {
			if (data?.order && (data?.type == 'update' || data?.type == 'created')) {
				console.log('LISTEN', data);
				if (
					data?.order?.status == OrderStatus.IN_PREPARATION &&
					this.filterOrderChannel([data.order], this.orderChannels).length >
						0 &&
					this.soundOn$$.getValue()
				) {
					this.playClickSound();
				}
				this.updateState(data.order);
			}
		});
		this.socket.on('venue-' + venueId + '-order-pools', (data: any) => {
			console.log('LISTEN pools', data);

			if (data?.orderPool && data?.type == 'updated') {
				console.log('LISTEN pools', data);

				if (
					data?.orderPool?.orderStatus == OrderStatus.IN_PREPARATION &&
					this.soundOn$$.getValue()
				) {
					this.playClickSound();
				}

				this.managerFacade.addOrderPoolNew(data?.orderPool);
			}
		});
	}
	private updateState(order: Order) {
		this.managerFacade.updateOrder(order);
		try {
			if (order?.status === OrderStatus.IN_PREPARATION) {
				const venue = this.venueData$.getValue();
				if (this.printer && this.printer?.isConnected && venue) {
					this.printer.print(order, venue);
				}
			}
		} catch (e) {}
	}
	private authenticateSocket(data: {
		roles: SocketRole[];
		venueIds: string[];
	}): void {
		this.socket.emit('authenticate', data.roles, data.venueIds, (ack: any) => {
			console.log('Socket authenticated for ', ack, data);
		});
	}
	private listenSocket(tables: Table[]) {
		console.log('load Socket');
		// tables.forEach(it => {
		// 	this.socket.on(
		// 		`terminalStatus_${it.venue}_${it.number.replace(' ', '_')}`,
		// 		(data: any) => {
		// 			console.log('EMIT STATUS', data);
		// 			this.managerFacade.updateTable({...it, status: data ?? 'Failed'});
		// 		}
		// 	);
		// });
	}
	updateStatusOrderPool(data: {orderPool: OrderPool; status: STATUS_CHANGE}) {
		console.log(
			'HERE UPDATE POOL',
			data,
			this.orderService.getNextOrderStatus(data.orderPool.orderStatus)
		);
		if (!data || !data.orderPool || !data.status) {
			return;
		}
		switch (data.status) {
			case STATUS_CHANGE.PREV:
				// this.managerFacade.updateOrderPool(
				// 	data.orderPool,
				// 	this.orderService.getPreviousOrderStatus(data.orderPool.orderStatus)
				// );
				break;
			case STATUS_CHANGE.NEXT:
				this.managerFacade.updateOrderPool(
					data.orderPool,
					this.orderService.getNextOrderStatus(data.orderPool.orderStatus)
				);
				break;
			default:
				break;
		}
	}
	updateStatus(data: {order: Order; status: STATUS_CHANGE}) {
		if (!data || !data.order || !data.status) {
			return;
		}
		switch (data.status) {
			case STATUS_CHANGE.PREV:
				//TODO: fix when needed
				// this.managerFacade.updateOrderStatus(
				// 	data.order,
				// 	this.orderService.getPreviousOrderStatus(data.order.status)
				// );
				break;
			case STATUS_CHANGE.NEXT:
				this.managerFacade.updateOrderStatus(
					data.order,
					this.orderService.getNextOrderStatus(data.order.status)
				);
				break;
			default:
				break;
		}
	}
	stockManager() {
		this.navService.stockManager();
	}

	checkIfOrder(value: Order | OrderPool): value is Order {
		return (value as Order)?.orderedArticles != undefined;
	}

	scrollLeft() {
		this.scrollableContent.nativeElement.scrollBy({
			left: -500,
			behavior: 'smooth'
		});
	}

	scrollRight() {
		this.scrollableContent.nativeElement.scrollBy({
			left: 500,
			behavior: 'smooth'
		});
	}
	sendToPOSOrder(order: Order) {
		if (!this.loadingOrders.includes(order._id)) {
			this.loadingOrders.push(order._id);
			this.apiService
				.sendOrderToPOS(order)
				.pipe(
					catchError(error => {
						this.toaster.error(
							'Please enter the order manually into the POS.',
							'Something went wrong.',
							{
								timeOut: 5000
							}
						);
						return of(null);
					})
				)
				.subscribe(v => {
					if (v) {
						this.managerFacade.updateOrder(v);
					}
					this.loadingOrders = this.loadingOrders.filter(it => it != order._id);
				});
		}
	}

	playClickSound() {
		console.log('----SOUND----');

		const audio = new Audio('../../../assets/sound.mp3');
		audio.load(); // This ensures the audio is preloaded
		audio.currentTime = 0;
		audio.play();
	}

	filterOrderChannel(orders: Order[], channels: OrderChannelsFilter): Order[] {
		if (channels.length === 0) return orders;

		const channelSet = new Set(channels);
		const includeDelivery = channelSet.has('delivery');
		const includePreorder = channelSet.has(OrderType.PREORDER);
		const filteredTypes = channels.filter(
			c => c !== 'delivery' && c !== OrderType.PREORDER
		) as (OrderType | PaymentMethod)[];

		return orders.filter(order => {
			const isStandardMatch =
				filteredTypes.includes(order.method) ||
				filteredTypes.includes(order.type);

			const isPreorder =
				includePreorder &&
				order.type === OrderType.PREORDER &&
				![
					PaymentMethod.WOLT,
					PaymentMethod.UBER_EATS,
					PaymentMethod.JET
				].includes(order.method);

			const isDelivery =
				includeDelivery &&
				order.type === OrderType.PREORDER &&
				order.preorder.type === PreorderType.DELIVERY;

			return isStandardMatch || isPreorder || isDelivery;
		});
	}

	toggleSound(newState: boolean) {
		this.soundOn$$.next(newState);
		localStorage.setItem(this.soundOnKey, JSON.stringify(newState));
	}

	// private pollPosStatus(
	// 	venueId: string | undefined,
	// 	destroy$: Observable<void>
	// ): Observable<boolean | null> {
	// 	const timeInterval = 1 * 60 * 1000; // 3 minutes in milliseconds
	// 	return interval(3 * 60 * 1000).pipe(
	// 		startWith(0),
	// 		switchMap(() => {
	// 			if (!venueId) {
	// 				return of(null);
	// 			}
	// 			return this.apiService.checkPosStatus(venueId).pipe(
	// 				map(responseDate => {
	// 					return moment(responseDate)
	// 						.add(timeInterval - 10000, 'milliseconds')
	// 						.isAfter(moment());
	// 				}),
	// 				catchError(err => {
	// 					console.error('POS check error:', err);
	// 					return of(null);
	// 				})
	// 			);
	// 		}),
	// 		takeUntil(destroy$)
	// 	);
	// }
}
